import { useState } from 'react';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { 
  MapPin, Phone, Clock, Navigation, MessageSquare, CheckCircle2, 
  Globe, Star, ExternalLink, ShieldCheck, Zap, Building2, Compass, Award
} from 'lucide-react';
import { 
  createLocalBusinessSchema, 
  createCollectionPageSchema, 
  createBreadcrumbListSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';
import { Button } from '@/components/ui/button';

export default function LocalBusinessPage() {
  const [selectedCity, setSelectedCity] = useState('all'); // 'all' | 'gurgaon' | 'sohna' | 'delhi-ncr'

  const breadcrumbItems = [
    { label: 'Print Centers', url: '/print-centers' }
  ];

  // Locations Data Array with full Local SEO attributes
  const locations = [
    {
      id: 'sohna',
      cityName: 'Sohna / Campus',
      region: 'Haryana',
      title: 'Inchpaper Print Kiosk - GD Goenka University Campus',
      address: 'C Block, GD Goenka University, Sohna, Haryana - 122103',
      geo: { lat: 28.2476, lng: 77.0628, display: '28.2476° N, 77.0628° E' },
      phone: '+91-7703860982',
      email: 'info@inchpaper.com',
      hours: '24x7 Self-Service Automated Kiosk',
      physicalDeskHours: 'Mon - Sat: 9:00 AM - 6:00 PM',
      googleMapsUrl: 'https://maps.app.goo.gl/hcQdb9yTzxiHEDAK9',
      googleBusinessProfileUrl: 'https://maps.app.goo.gl/hcQdb9yTzxiHEDAK9',
      mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3513.23812852033!2d77.060211!3d28.2476!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjhCsDE0JzUxLjQiTiA3N8KwMDMnNDYuMSJF!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin',
      rating: 4.9,
      reviewCount: 640,
      serviceAreas: ['GD Goenka University', 'Sohna Town', 'KR Mangalam Campus', 'Sohna Road Corridor', 'Damdama Lake Area']
    },
    {
      id: 'gurgaon',
      cityName: 'Gurgaon',
      region: 'Haryana',
      title: 'Inchpaper Print Center & Head Kiosk - Sadar Bazaar Gurgaon',
      address: '575/7, 1st Floor, Inderpuri, Jacobpura, Sadar Bazaar, Gurgaon, Haryana - 122001',
      geo: { lat: 28.4595, lng: 77.0266, display: '28.4595° N, 77.0266° E' },
      phone: '+91-7703860982',
      email: 'info@inchpaper.com',
      hours: '24x7 Online Print Acceptance & Express Pickup',
      physicalDeskHours: 'Mon - Sat: 9:00 AM - 7:00 PM',
      googleMapsUrl: 'https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8',
      googleBusinessProfileUrl: 'https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8',
      mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.828212!2d77.0266!3d28.4595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjhCsDI3JzM0LjIiTiA3N8KwMDEnMzU4LjAiRQ!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin',
      rating: 4.9,
      reviewCount: 640,
      serviceAreas: ['Cyber City Gurgaon', 'Golf Course Road', 'Sector 48 & 49', 'MG Road', 'Udyog Vihar', 'Sadar Bazaar']
    }
  ];

  // Service Cities Covered Array
  const CITIES_SERVED = [
    { name: 'Gurgaon', state: 'Haryana', pin: '122001', status: 'Active 24x7 Hub', hubs: 3 },
    { name: 'Sohna', state: 'Haryana', pin: '122103', status: 'Campus Kiosk Active', hubs: 2 },
    { name: 'Delhi NCR', state: 'Delhi', pin: '110001', status: 'Same-Day Courier Delivery', hubs: 5 },
    { name: 'Noida', state: 'Uttar Pradesh', pin: '201301', status: 'Express Delivery Area', hubs: 2 },
    { name: 'Faridabad', state: 'Haryana', pin: '121001', status: 'Express Delivery Area', hubs: 2 },
    { name: 'Ghaziabad', state: 'Uttar Pradesh', pin: '201001', status: 'Standard Courier Area', hubs: 1 }
  ];

  const filteredLocations = selectedCity === 'all' 
    ? locations 
    : locations.filter(l => l.id === selectedCity || (selectedCity === 'delhi-ncr' && l.region === 'Haryana'));

  // Detailed Schema Generator
  const localBusinessSchemas = locations.map(loc => 
    createLocalBusinessSchema({
      name: loc.title,
      description: `24x7 self-service document printing kiosk at ${loc.address}. Instant PDF printouts, online xerox, photo prints, spiral binding & hardbound thesis printing.`,
      url: `https://print.inchpaper.com/print-centers#${loc.id}`,
      telephone: loc.phone,
      email: loc.email,
      address: {
        streetAddress: loc.address,
        addressLocality: loc.cityName,
        addressRegion: loc.region,
        postalCode: loc.id === 'sohna' ? '122103' : '122001',
        addressCountry: 'IN'
      },
      geo: {
        latitude: loc.geo.lat,
        longitude: loc.geo.lng
      },
      googleMapsUrl: loc.googleMapsUrl,
      googleBusinessProfileUrl: loc.googleBusinessProfileUrl,
      areaServed: loc.serviceAreas.map(area => ({ "@type": "Place", "name": area }))
    })
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased text-slate-900">
      <SEOHead
        title="Print Centers & Google Maps Locations | Inchpaper Self-Service Kiosks"
        description="Find 24x7 Inchpaper self-service print kiosks in Gurgaon, Sohna & Delhi NCR. View Google Maps directions, geo coordinates, opening hours, and service areas."
        keywords="inchpaper print kiosk, google maps print center, printing service gurgaon, document print shop sohna, xerox near me, 24 hour print kiosk"
        canonical="https://print.inchpaper.com/print-centers"
        geoPosition="28.4595;77.0266"
        geoPlacename="Gurgaon, Haryana, India"
        geoRegion="IN-HR"
        schemas={[
          ...localBusinessSchemas, 
          createCollectionPageSchema({
            name: "Inchpaper Self-Service Print Centers Directory",
            description: "Directory of 24x7 self-service document printing kiosks across Gurgaon, Delhi NCR, and university campuses.",
            url: "https://print.inchpaper.com/print-centers",
            hasPart: locations.map(l => ({ title: l.title, url: `/print-centers#${l.id}` }))
          }),
          createBreadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Print Centers", url: "/print-centers" }
          ]),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Local Business Print Centers",
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Page Hero Header */}
        <div className="bg-white p-6 sm:p-10 rounded-3xl border border-slate-200/90 shadow-xs mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 pb-8">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-red-50 text-[#7d0909] px-3 py-1 rounded-full text-xs font-extrabold tracking-wide uppercase">
                <Building2 className="w-3.5 h-3.5" />
                <span>24x7 Local Business & Kiosk Directory</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Self-Service Print Centers & Locations
              </h1>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Locate automated 24x7 Inchpaper document kiosks, view exact Google Maps directions, GPS coordinates, operating hours, and local campus service areas.
              </p>
            </div>

            {/* Google Business Profile Badge */}
            <a 
              href="https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-slate-900 hover:bg-slate-800 text-white p-5 rounded-2xl shadow-md space-y-2 shrink-0 transition-all group"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Google Business Profile</span>
                <ExternalLink className="w-4 h-4 text-amber-400 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black text-amber-400">4.9</span>
                <div className="flex items-center text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400" />
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-slate-300 font-medium">
                1,280+ Verified Google Reviews
              </p>
            </a>
          </div>

          {/* City Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 pt-6">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-2">Filter City Hub:</span>
            {[
              { id: 'all', label: 'All Locations (2)' },
              { id: 'gurgaon', label: 'Gurgaon Headquarters' },
              { id: 'sohna', label: 'Sohna / GD Goenka Campus' },
              { id: 'delhi-ncr', label: 'Delhi NCR Area' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setSelectedCity(tab.id)}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${
                  selectedCity === tab.id
                    ? 'bg-[#7d0909] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* LOCATIONS LIST WITH GOOGLE MAPS EMBED & LOCAL SEO DATA */}
        <div className="space-y-8 mb-12">
          {filteredLocations.map(loc => (
            <div key={loc.id} id={loc.id} className="bg-white rounded-3xl border border-slate-200/90 shadow-xs overflow-hidden">
              
              <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* Left Column: Local Business Details */}
                <div className="lg:col-span-7 space-y-6">
                  
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-emerald-100 text-emerald-800 text-[11px] font-extrabold px-2.5 py-0.5 rounded-md flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Open Now • 24x7 Kiosk
                        </span>
                        <span className="bg-slate-100 text-slate-700 text-[11px] font-bold px-2.5 py-0.5 rounded-md">
                          {loc.cityName}
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                        {loc.title}
                      </h2>
                    </div>
                  </div>

                  {/* Attributes Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1.5">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-[#7d0909]" />
                        <span>Address & Landmark</span>
                      </div>
                      <p className="text-slate-600 leading-relaxed font-medium">
                        {loc.address}
                      </p>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1.5">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <Compass className="w-4 h-4 text-indigo-600" />
                        <span>GPS Geo Coordinates</span>
                      </div>
                      <p className="font-mono text-indigo-700 font-bold">
                        {loc.geo.display}
                      </p>
                      <p className="text-[10px] text-slate-500">Lat: {loc.geo.lat} | Long: {loc.geo.lng}</p>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1.5">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-emerald-600" />
                        <span>Operating Hours</span>
                      </div>
                      <p className="text-slate-700 font-bold">
                        {loc.hours}
                      </p>
                      <p className="text-[11px] text-slate-500">Physical Support Desk: {loc.physicalDeskHours}</p>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1.5">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <Phone className="w-4 h-4 text-[#7d0909]" />
                        <span>Helpdesk Contact</span>
                      </div>
                      <p className="text-slate-700 font-bold">
                        WhatsApp: {loc.phone}
                      </p>
                      <p className="text-[11px] text-slate-500">Email: {loc.email}</p>
                    </div>

                  </div>

                  {/* Service Area Badges */}
                  <div className="space-y-2">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Local Service Areas Covered:</span>
                    <div className="flex flex-wrap gap-2">
                      {loc.serviceAreas.map((area, idx) => (
                        <span key={idx} className="bg-slate-100 text-slate-800 text-xs font-medium px-3 py-1 rounded-lg border border-slate-200">
                          📍 {area}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-2">
                    <a
                      href={loc.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#7d0909] hover:bg-red-800 text-white text-xs font-bold px-5 py-3 rounded-xl shadow-xs inline-flex items-center gap-2 transition-colors"
                    >
                      <Navigation className="w-4 h-4" />
                      <span>Get Google Maps Directions</span>
                    </a>

                    <a
                      href={`https://wa.me/917703860982?text=Hello%20Inchpaper%20Support%20regarding%20${encodeURIComponent(loc.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-5 py-3 rounded-xl shadow-xs inline-flex items-center gap-2 transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>WhatsApp Location Support</span>
                    </a>
                  </div>

                </div>

                {/* Right Column: Google Maps Embed Frame */}
                <div className="lg:col-span-5 h-full min-h-[320px] rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 relative">
                  <iframe 
                    title={`Google Map - ${loc.title}`}
                    src={loc.mapEmbedUrl}
                    width="100%" 
                    height="100%" 
                    style={{ border: 0, minHeight: '320px' }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-xs p-3 rounded-xl border border-slate-200 text-xs flex items-center justify-between">
                    <span className="font-bold text-slate-900">Verified Google Business Marker</span>
                    <a href={loc.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-[#7d0909] font-bold flex items-center gap-1 hover:underline">
                      View Map <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

              </div>

            </div>
          ))}
        </div>

        {/* CITIES & REGIONAL SERVICE AREA MATRIX */}
        <div className="bg-white p-6 sm:p-10 rounded-3xl border border-slate-200/90 shadow-xs mb-12 space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#7d0909]" />
                <span>Cities & Regional Service Area Matrix</span>
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Same-day doorstep delivery, express kiosk pickup & institutional printing across Delhi NCR cities.
              </p>
            </div>
            <span className="bg-slate-100 text-slate-800 text-xs font-bold px-3 py-1 rounded-full border border-slate-200">
              Coverage: Delhi NCR & Haryana
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            {CITIES_SERVED.map((c, i) => (
              <div key={i} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-sm text-slate-900">{c.name}</span>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-extrabold px-2 py-0.5 rounded-sm">
                    PIN {c.pin}
                  </span>
                </div>
                <div className="text-slate-600 font-medium">Region: {c.state}</div>
                <div className="text-emerald-700 font-bold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GOOGLE BUSINESS PROFILE & REVIEW HIGHLIGHTS */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-red-950 text-white p-8 sm:p-10 rounded-3xl shadow-xl space-y-6 mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-xl">
              <span className="text-amber-400 font-bold text-xs uppercase tracking-wider block">Official Google Business Profile</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                Rate & Review Inchpaper on Google Maps
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                We are proud to serve thousands of students, corporate professionals, and residents in Gurgaon and Sohna with 4.9⭐ average satisfaction.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href="https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-amber-400 hover:bg-amber-500 text-slate-950 font-extrabold text-xs px-6 py-3.5 rounded-xl shadow-md inline-flex items-center justify-center gap-2 transition-all"
              >
                <Star className="w-4 h-4 fill-slate-950" />
                <span>Write a Google Review</span>
              </a>

              <a 
                href="https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs px-6 py-3.5 rounded-xl border border-slate-700 inline-flex items-center justify-center gap-2 transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                <span>View Google Business Profile</span>
              </a>
            </div>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}
